import { devLog } from './devLog';
import {
  getActiveTransactionLockWindow,
  getNextFcfsClose,
  getNextWaiverRun,
  getNowEastern,
  getWeek1ScoringStartsEastern,
  isClaimQueuePeriod,
  isFcfsOpen,
  WEEK_1_SCORING_START_SHORT,
} from './seasonCalendar';

// Transaction phases: FCFS (first come, first served) Wed unlock → Sun 1 PM vs claim queue Sun 1 PM → Wed unlock.
// Lineups use per-slot kickoff reveal locks.

const ET_TIMEZONE = 'America/New_York';

function getEasternNow() {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: ET_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const parts = formatter.formatToParts(now);
  const get = (type) => parseInt(parts.find(p => p.type === type).value, 10);
  const year = get('year');
  const month = get('month') - 1;
  const day = get('day');
  return {
    year,
    month,
    day,
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
    weekday: new Date(year, month, day).getDay()
  };
}

export function getCurrentEST() {
  const e = getEasternNow();
  return new Date(e.year, e.month, e.day, e.hour, e.minute, e.second);
}

export function isNFLSeason() {
  const e = getEasternNow();
  const month = e.month + 1;
  return month >= 9 || month <= 1;
}

/** True during claim-queue period (no FCFS / trades). */
export function isRosterLocked(unlockDay = 'wednesday', unlockHour = 5) {
  if (typeof window !== 'undefined' && localStorage.getItem('DISABLE_ROSTER_LOCKS') === 'true') {
    return false;
  }
  if (!isNFLSeason()) return false;
  const now = getNowEastern();
  if (now < getWeek1ScoringStartsEastern()) return false;
  return isClaimQueuePeriod(unlockDay, unlockHour, now);
}

export { isFcfsOpen, isClaimQueuePeriod };

function formatCountdown(diffMs) {
  if (diffMs <= 0) return '0h 0m 0s';
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const secs = Math.floor((diffMs % (1000 * 60)) / 1000);
  return `${hours}h ${mins}m ${secs}s`;
}

export function getLockCountdown(unlockDay = 'wednesday', unlockHour = 5) {
  if (typeof window !== 'undefined' && localStorage.getItem('DISABLE_ROSTER_LOCKS') === 'true') {
    return { isLocked: false, isFcfsOpen: true, countdown: "Testing Mode - Locks Disabled", nextEvent: "NFL Season" };
  }
  if (!isNFLSeason()) {
    return { isLocked: false, isFcfsOpen: true, countdown: "Off-season", nextEvent: "Week 1 scoring" };
  }

  const now = getNowEastern();
  const seasonStart = getWeek1ScoringStartsEastern();
  if (seasonStart && now < seasonStart) {
    return {
      isLocked: false,
      isFcfsOpen: true,
      countdown: "Preseason",
      nextEvent: `Week 1 (${WEEK_1_SCORING_START_SHORT})`,
    };
  }

  const fcfsOpen = isFcfsOpen(unlockDay, unlockHour, now);
  const hour = unlockHour == null ? 5 : Number(unlockHour);
  const unlockLabel = `${(unlockDay || 'wednesday').charAt(0).toUpperCase()}${(unlockDay || 'wednesday').slice(1)} ${hour}:00 ET`;

  if (!fcfsOpen) {
    const activeLockWindow = getActiveTransactionLockWindow(now, unlockDay, unlockHour);
    const nextRun = activeLockWindow?.unlockTime || getNextWaiverRun(unlockDay, unlockHour, now);
    const diff = nextRun ? nextRun - now : 0;
    return {
      isLocked: true,
      isFcfsOpen: false,
      countdown: formatCountdown(diff),
      nextEvent: `Waiver run (${unlockLabel})`
    };
  }

  const nextClose = getNextFcfsClose(now);
  const diff = nextClose ? nextClose - now : 0;
  return {
    isLocked: false,
    isFcfsOpen: true,
    countdown: formatCountdown(diff),
    nextEvent: "FCFS closes (first come, first served) — Sun 1:00 PM ET",
  };
}

export function getLockStatusMessage(unlockDay = 'wednesday', unlockHour = 5) {
  const { isLocked, isFcfsOpen: fcfs, countdown, nextEvent } = getLockCountdown(unlockDay, unlockHour);
  if (typeof window !== 'undefined' && localStorage.getItem('DISABLE_ROSTER_LOCKS') === 'true') {
    return "🧪 TESTING MODE - Roster locks disabled for testing";
  }
  if (!isNFLSeason()) {
    return "🔓 OFF-SEASON — First come, first served (FCFS) pickups and trades open";
  }
  const now = getNowEastern();
  const seasonStart = getWeek1ScoringStartsEastern();
  if (seasonStart && now < seasonStart) {
    return `🔓 PRESEASON — First come, first served (FCFS) pickups open; weekly waiver windows start ${WEEK_1_SCORING_START_SHORT}`;
  }
  if (isLocked) {
    return `🔒 CLAIM QUEUE — ${nextEvent} in: ${countdown}`;
  }
  if (fcfs) {
    return `🔓 FCFS OPEN (first come, first served) — ${nextEvent} in: ${countdown}`;
  }
  return `🔓 Transactions open — ${nextEvent} in: ${countdown}`;
}

export function getCurrentNFLWeek() {
  const e = getEasternNow();
  const year = e.year;
  const month = e.month + 1;
  if (month < 9 && month > 1) return 0;
  const seasonYear = month >= 9 ? year : year - 1;
  const sepFirst = new Date(seasonYear, 8, 1);
  const thu = 4;
  let daysUntilThu = (thu - sepFirst.getDay() + 7) % 7;
  if (daysUntilThu === 0 && sepFirst.getDay() !== thu) daysUntilThu = 7;
  const week1Start = new Date(seasonYear, 8, 1 + daysUntilThu);
  const now = new Date(e.year, e.month, e.day);
  if (now < week1Start) return 0;
  const delta = Math.floor((now - week1Start) / (24 * 60 * 60 * 1000));
  const weekNum = Math.floor(delta / 7) + 1;
  return Math.max(1, Math.min(18, weekNum));
}

export function isPastTradeDeadline(tradeDeadlineWeek) {
  const deadline = tradeDeadlineWeek != null ? Number(tradeDeadlineWeek) : 11;
  const current = getCurrentNFLWeek();
  if (current === 0) return false;
  return current > deadline;
}

if (typeof window !== 'undefined') {
  window.disableRosterLocks = () => {
    localStorage.setItem('DISABLE_ROSTER_LOCKS', 'true');
    devLog('✅ Roster locks DISABLED for testing. Refresh the page to see changes.');
    return 'Roster locks disabled. Refresh the page.';
  };
  window.enableRosterLocks = () => {
    localStorage.removeItem('DISABLE_ROSTER_LOCKS');
    devLog('✅ Roster locks ENABLED. Refresh the page to see changes.');
    return 'Roster locks enabled. Refresh the page.';
  };
  window.checkLockStatus = (unlockDay, unlockHour) => {
    const disabled = localStorage.getItem('DISABLE_ROSTER_LOCKS') === 'true';
    const status = getLockCountdown(unlockDay, unlockHour);
    devLog('Lock Status:', { disabled, ...status, message: getLockStatusMessage(unlockDay, unlockHour) });
    return status;
  };
}
